import {
  isSameSubject,
  normalizeSubject,
  type Message,
  type TimelineItem,
} from "@catmail/shared";

import { formatDateChip, isSameDay } from "@/lib/datetime";

/**
 * 메시지 목록에 날짜 칩과 제목 줄을 끼워 넣는다.
 *
 * 제목 줄이 이 앱의 핵심 장치다. 메일은 제목이 바뀌면 보통 다른 스레드로
 * 갈라지지만, 여기서는 같은 방에 남기고 한 줄만 넣는다. 사람 기준으로 대화를
 * 보는 것이 목적이기 때문이다.
 *
 * **제목이 같은 메시지 묶음마다 그 앞에 한 줄이 붙는다.** 첫 묶음도 예외가
 * 아니다 — 예전에는 "방 제목이 곧 그 제목"이라며 건너뛰었는데, 방 제목은
 * 사실 **사람 이름**이라 대화의 첫 제목이 화면 어디에도 나오지 않았다.
 * 받은 메일의 제목을 못 보는 메일 앱이었던 셈이다.
 *
 * 건너뛰지 않으면 덤도 있다. 위로 스크롤해 옛 메시지를 더 불러올 때, 억눌려
 * 있던 줄이 갑자기 나타나는 일이 없어진다. "같은 제목 묶음 앞에 한 줄"이라는
 * 규칙이 창을 어디서 자르든 똑같이 성립하기 때문이다.
 *
 * @param notice 방 전체에 대한 안내. 맨 위에 한 줄로 붙는다.
 * @param subjectInBubble 말풍선이 제목을 직접 이고 있는가(조용한 방).
 */
export function buildTimeline(
  messages: Message[],
  notice?: string | null,
  /**
   * 조용한 방은 말풍선 첫 줄이 곧 제목이다(MessageBubble). 그럴 때 제목 줄까지
   * 넣으면 **같은 문장이 위아래로 두 번** 나온다. 광고 메일은 통마다 제목이
   * 달라서 줄도 통마다 생기므로, 대화가 구분선 반 제목 반이 된다.
   */
  subjectInBubble = false,
): TimelineItem[] {
  const items: TimelineItem[] = [];
  if (messages.length === 0) return items;

  const first = messages[0];
  if (notice && first) {
    items.push({ kind: "notice", at: first.sentAt, text: notice });
  }

  let previousAt: string | null = null;
  let previousSubject: string | null = null;

  for (const message of messages) {
    if (previousAt === null || !isSameDay(previousAt, message.sentAt)) {
      items.push({
        kind: "dateChip",
        at: message.sentAt,
        label: formatDateChip(message.sentAt),
      });
    }

    // Re:·Fwd:를 걷어낸 값으로 비교한다. 답장마다 줄이 생기면 안 된다.
    // 다만 **보여줄 때는 원문 그대로** 쓴다 — 상대 메일함에 뜨는 것이 그것이다.
    const subject = normalizeSubject(message.subject);
    if (subject !== previousSubject && !subjectInBubble) {
      items.push({
        kind: "subjectShift",
        at: message.sentAt,
        subject: message.subject,
        // 첫 줄인지. 문구가 달라진다 — 첫 제목에 "다른 이야기"는 말이 안 된다.
        first: previousSubject === null,
      });
    }

    items.push({ kind: "message", at: message.sentAt, message });

    previousAt = message.sentAt;
    previousSubject = subject;
  }

  return items;
}

/**
 * 이만큼 안에 이어서 온 것만 한 덩어리로 본다.
 *
 * 메신저라면 몇 초 단위지만 여기서는 말풍선 하나가 **메일 한 통**이라 간격이
 * 훨씬 넓다. 그래도 상한은 있어야 한다 — 아침에 온 메일과 저녁에 온 메일을
 * 붙여 놓으면 한 번에 쏟아낸 말처럼 읽히고, 아바타와 이름이 지워져 언제 다시
 * 말을 건 것인지 알 수 없다.
 */
const CONTINUATION_WINDOW_MS = 10 * 60 * 1000;

/**
 * 같은 사람이 연달아 보낸 메시지인가.
 * 두 번째부터는 이름과 아바타를 지워서 말풍선만 이어 붙인다(시안 03).
 *
 * 사람만 보는 게 아니라 **시간도 본다.** 사이에 날짜 칩이나 제목 줄이 끼면
 * 자연히 끊기지만, 같은 날 같은 제목으로 몇 시간 뒤에 온 메일은 그것만으로는
 * 안 끊긴다.
 */
export function isContinuation(
  items: TimelineItem[],
  index: number,
): boolean {
  const current = items[index];
  const previous = items[index - 1];

  if (current?.kind !== "message" || previous?.kind !== "message") return false;
  if (current.message.senderId !== previous.message.senderId) return false;

  // 제목이 바뀌면 다른 이야기다. 보통은 사이에 제목 줄이 끼어 자연히 끊기지만,
  // 조용한 방처럼 그 줄을 넣지 않는 곳에서는 여기서 직접 봐야 한다.
  if (!isSameSubject(current.message.subject, previous.message.subject)) {
    return false;
  }

  const gap =
    Date.parse(current.message.sentAt) - Date.parse(previous.message.sentAt);
  // 시각이 이상한 메일(파싱 실패)은 끊지 않는다. 붙는 쪽이 덜 튄다.
  if (!Number.isFinite(gap)) return true;

  return gap <= CONTINUATION_WINDOW_MS;
}
